import { useState, useRef, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { PageHeader } from "../components/AppLayout";
import { StatusBadge } from "../components/StatusBadge";
import { Moon, Send, Hand, Phone, Globe, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { CONVERSACIONES, PROPIEDADES, agentePorId } from "../data/mockData";
import { respuestaAsistente } from "../data/chatScripts";

export default function Asistente() {
  const [convs, setConvs] = useState(CONVERSACIONES);
  const [sel, setSel] = useState(CONVERSACIONES[0]);
  const [filtro, setFiltro] = useState("todas");

  const tomar = (id) => {
    setConvs((cs) =>
      cs.map((c) =>
        c.id === id ? { ...c, tomada: true, estado: "Tomada por agente", agenteId: "ag-1" } : c
      )
    );
    setSel((s) => (s.id === id ? { ...s, tomada: true, estado: "Tomada por agente", agenteId: "ag-1" } : s));
    toast.success("Tomaste la conversación. Ahora seguís vos a mano.");
  };

  const lista = convs.filter((c) =>
    filtro === "fuera" ? c.fueraDeHorario : filtro === "tomadas" ? c.tomada : true
  );

  return (
    <div>
      <PageHeader
        titulo="Asistente 24/7"
        descripcion="Responde consultas de WhatsApp y web con la ficha de cada propiedad. Cuando la oficina cierra (18:00), sigue respondiendo y ofrece agendar visita."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bandeja */}
        <Card className="rounded-lg border-slate-200 shadow-sm lg:col-span-1 flex flex-col max-h-[640px]">
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-slate-900">Bandeja de conversaciones</h3>
              <Badge variant="outline" className="rounded-md">{lista.length}</Badge>
            </div>
            <Select value={filtro} onValueChange={setFiltro}>
              <SelectTrigger data-testid="assistant-filter" className="rounded-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="fuera">Fuera de horario</SelectItem>
                <SelectItem value="tomadas">Tomadas por agente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-y-auto divide-y divide-slate-100" data-testid="assistant-inbox-list">
            {lista.map((c) => (
              <button
                key={c.id}
                onClick={() => setSel(c)}
                data-testid={`conversation-${c.id}`}
                className={`w-full text-left px-4 py-3 transition-colors hover:bg-slate-50 ${
                  sel.id === c.id ? "bg-brand-light" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-900 truncate">{c.contacto.nombre}</span>
                  <span className="flex items-center gap-1 text-[11px] text-slate-400 shrink-0">
                    {c.canal === "WhatsApp" ? <Phone className="h-3 w-3" /> : <Globe className="h-3 w-3" />}
                    {c.horaActividad}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-slate-500 truncate">{c.propiedadTitulo}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {c.fueraDeHorario && (
                    <Badge
                      data-testid="assistant-outside-hours-badge"
                      variant="outline"
                      className="rounded-md bg-purple-50 text-purple-800 border-purple-200 text-[10px] font-medium"
                    >
                      <Moon className="mr-1 h-3 w-3" /> Respondida fuera de horario
                    </Badge>
                  )}
                  {c.tomada && (
                    <Badge variant="outline" className="rounded-md bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">
                      Tomada
                    </Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Detalle + simulador */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-lg border-slate-200 shadow-sm flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">{sel.contacto.nombre}</p>
                <p className="text-xs text-slate-500 truncate">
                  {sel.contacto.telefono} · {sel.propiedadTitulo}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge estado={sel.tomada ? "Tomada por agente" : "Respondida por asistente"}
                  className={sel.tomada ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"} />
                {!sel.tomada && (
                  <Button
                    size="sm"
                    className="btn-brand rounded-md"
                    data-testid="assistant-take-chat-button"
                    onClick={() => tomar(sel.id)}
                  >
                    <Hand className="mr-1.5 h-4 w-4" /> Tomar conversación
                  </Button>
                )}
              </div>
            </div>
            <div className="p-5 space-y-3 bg-slate-50 max-h-72 overflow-y-auto">
              {sel.mensajes.map((m, i) => (
                <div key={i} className={`flex ${m.de === "cliente" ? "justify-start" : "justify-end"}`}>
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                      m.de === "cliente"
                        ? "bg-white border border-slate-200 text-slate-800"
                        : "bg-brand text-white"
                    }`}
                  >
                    <p>{m.texto}</p>
                    <p className={`mt-1 text-[10px] ${m.de === "cliente" ? "text-slate-400" : "text-white/70"}`}>
                      {m.de === "bot" ? "Asistente" : m.de === "agente" ? "Agente" : "Cliente"} · {m.hora}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {sel.ofrecioVisita && (
              <div className="px-5 py-2 border-t border-slate-100 bg-emerald-50 text-xs text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> El asistente ofreció coordinar una visita en esta conversación.
              </div>
            )}
          </Card>

          <Simulador />
        </div>
      </div>
    </div>
  );
}

function Simulador() {
  const [propId, setPropId] = useState(PROPIEDADES[0].id);
  const prop = PROPIEDADES.find((p) => p.id === propId);
  const [mensajes, setMensajes] = useState([
    {
      de: "bot",
      texto:
        "🌙 Son las 21:30 y la oficina está cerrada, pero te sigo atendiendo. Preguntame lo que quieras de esta propiedad: precio, expensas, m², requisitos, mascotas o coordinemos una visita.",
    },
  ]);
  const [texto, setTexto] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
  }, [mensajes]);

  const enviar = () => {
    if (!texto.trim()) return;
    const userMsg = { de: "cliente", texto };
    const resp = respuestaAsistente(texto, prop);
    setMensajes((m) => [...m, userMsg, { de: "bot", texto: resp.texto }]);
    setTexto("");
  };

  const sugerencias = ["¿Cuánto sale?", "¿Acepta mascotas?", "¿Qué requisitos piden?", "Quiero visitarlo"];

  return (
    <Card className="rounded-lg border-slate-200 shadow-sm">
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2 mb-1">
          <Moon className="h-4 w-4 text-purple-600" />
          <h3 className="text-base font-semibold text-slate-900">Simulador — escribí como un interesado a las 21:30</h3>
        </div>
        <p className="text-xs text-slate-500">Respuestas guionadas sobre la ficha de la propiedad (no usa IA real).</p>
        <div className="mt-3">
          <Select value={propId} onValueChange={(v) => setPropId(v)}>
            <SelectTrigger data-testid="simulator-property-select" className="rounded-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROPIEDADES.slice(0, 15).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.titulo} · {p.direccion}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div ref={scrollRef} className="p-5 space-y-3 bg-slate-50 max-h-80 overflow-y-auto">
        {mensajes.map((m, i) => (
          <div key={i} className={`flex ${m.de === "cliente" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                m.de === "cliente" ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-800"
              }`}
            >
              {m.texto}
            </div>
          </div>
        ))}
      </div>
      <div className="px-5 py-3 border-t border-slate-100">
        <div className="flex flex-wrap gap-2 mb-3">
          {sugerencias.map((s) => (
            <button
              key={s}
              onClick={() => setTexto(s)}
              className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600 hover:border-slate-300"
            >
              {s}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && enviar()}
            placeholder="Escribí tu consulta…"
            className="rounded-md"
            data-testid="assistant-chat-simulator-input"
          />
          <Button onClick={enviar} className="btn-brand rounded-md" data-testid="simulator-send-button">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
